import type { CollectionMode } from "pages/CampaignPage/campaignKind";

/** Innsamlet i én kategori. `category: null` = gaver uten kategori. */
export interface CategorySummary {
  category: string | null;
  raisedOre: number;
  chargeCount?: number;
  donorCount: number;
  activeMonthlyCount: number;
}

/** Beløp er alltid i øre (NOK). */
export interface CampaignSummary {
  campaignId: number;
  name: string;
  collectionMode?: CollectionMode | string | null;
  /** Mål i kroner. */
  targetAmount?: number | null;
  raisedOre: number;
  donorCount: number;
  activeMonthlyCount: number;
  activated?: boolean | null;
  byCategory?: CategorySummary[] | null;
}

export interface MonthSummary {
  /** «2026-10» */
  month: string;
  raisedOre: number;
  chargeCount: number;
}

export interface ProviderSummary {
  provider: string;
  raisedOre: number;
}

export interface MosqueSummary {
  orgNr: string;
  name: string;
  raisedOre: number;
  donorCount: number;
  activeMonthlyCount: number;
}

/**
 * `GET /api/v1/mosques/{orgNr}/donations/summary` og
 * `GET /api/v1/admin/donations/summary` (den siste har også `byMosque`).
 */
export interface DonationSummary {
  currency?: string;
  totalOre: number;
  oneTimeOre?: number;
  monthlyOre?: number;
  paidChargeCount?: number;
  donorCount: number;
  activeMonthlyCount: number;
  activeMonthlyOre: number;
  byCampaign?: CampaignSummary[] | null;
  byMonth?: MonthSummary[] | null;
  byProvider?: ProviderSummary[] | null;
  byMosque?: MosqueSummary[] | null;
  byCategory?: CategorySummary[] | null;
}

export interface DateRange {
  /** «YYYY-MM-DD», eller tom for «fra start». */
  from?: string;
  to?: string;
}

export const donationSummaryUrl = (orgNr: string | null) =>
  orgNr
    ? `/api/v1/mosques/${encodeURIComponent(orgNr)}/donations/summary`
    : "/api/v1/admin/donations/summary";

/** Query-parametre; tomme datoer sendes ikke. */
export const rangeParams = (range: DateRange) => {
  const params: Record<string, string> = {};
  if (range.from) params.from = range.from;
  if (range.to) params.to = range.to;
  return params;
};

export type RangePreset = "all" | "thisYear" | "last12" | "custom";

export const RANGE_PRESETS: Array<{ value: RangePreset; label: string }> = [
  { value: "all", label: "Alt" },
  { value: "thisYear", label: "I år" },
  { value: "last12", label: "Siste 12 måneder" },
  { value: "custom", label: "Velg datoer" },
];

const pad = (n: number) => String(n).padStart(2, "0");

/** Lokal dato → «YYYY-MM-DD». */
export const isoDate = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const monthKey = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

/** Datoene for et forhåndsvalg. «Velg datoer» gir det som er skrevet inn. */
export const presetRange = (
  preset: RangePreset,
  now: Date,
  custom: DateRange = {},
): DateRange => {
  switch (preset) {
    case "thisYear":
      return { from: `${now.getFullYear()}-01-01`, to: isoDate(now) };
    case "last12": {
      const start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      return { from: isoDate(start), to: isoDate(now) };
    }
    case "custom":
      return { from: custom.from || undefined, to: custom.to || undefined };
    default:
      return {};
  }
};

/** «Fra-dato» etter «til-dato» er ugyldig. */
export const isRangeValid = (range: DateRange) =>
  !range.from || !range.to || range.from <= range.to;

const addMonths = (key: string, n: number) => {
  const [y, m] = key.split("-").map(Number);
  return monthKey(new Date(y, m - 1 + n, 1));
};

/**
 * Månedene som søyler, eldste først, uten hull. Serveren sender de siste 12
 * månedene; mangler noen (eller alle), fylles de inn med 0.
 */
export const fillMonths = (
  byMonth: MonthSummary[] | null | undefined,
  now: Date,
): MonthSummary[] => {
  const valid = (byMonth ?? []).filter((m) => /^\d{4}-\d{2}$/.test(m.month));
  const byKey = new Map(valid.map((m) => [m.month, m]));
  const sorted = Array.from(byKey.keys()).sort();
  const last = sorted.length ? sorted[sorted.length - 1] : monthKey(now);
  const first =
    sorted.length && sorted[0] < addMonths(last, -11)
      ? sorted[0]
      : addMonths(last, -11);
  const months: MonthSummary[] = [];
  for (let key = first; key <= last; key = addMonths(key, 1)) {
    months.push(byKey.get(key) ?? { month: key, raisedOre: 0, chargeCount: 0 });
  }
  return months;
};

const MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "mai",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "des",
];

const MONTHS_LONG = [
  "januar",
  "februar",
  "mars",
  "april",
  "mai",
  "juni",
  "juli",
  "august",
  "september",
  "oktober",
  "november",
  "desember",
];

/** «2026-10» → «okt». */
export const shortMonth = (key: string) =>
  MONTHS[Number(key.slice(5, 7)) - 1] ?? key;

/** «2026-10» → «oktober 2026». */
export const longMonth = (key: string) =>
  `${MONTHS_LONG[Number(key.slice(5, 7)) - 1] ?? key} ${key.slice(0, 4)}`;

/** Innsamlet i inneværende måned (0 hvis måneden mangler). */
export const thisMonthOre = (
  byMonth: MonthSummary[] | null | undefined,
  now: Date,
) => byMonth?.find((m) => m.month === monthKey(now))?.raisedOre ?? 0;

/** «VIPPS» → «Vipps», «STRIPE» → «Kort». */
export const providerName = (provider: string) =>
  provider === "VIPPS" ? "Vipps" : provider === "STRIPE" ? "Kort" : provider;

/** Vipps og kort alltid med (0 hvis tom), i den rekkefølgen, med andel i prosent. */
export const providerSplit = (
  byProvider: ProviderSummary[] | null | undefined,
) => {
  const sum = (p: string) =>
    (byProvider ?? [])
      .filter((x) => x.provider === p)
      .reduce((s, x) => s + (x.raisedOre || 0), 0);
  const rows = ["VIPPS", "STRIPE"].map((provider) => ({
    provider,
    raisedOre: sum(provider),
  }));
  const total = rows.reduce((s, r) => s + r.raisedOre, 0);
  return rows.map((r) => ({
    ...r,
    percent: total > 0 ? Math.round((r.raisedOre / total) * 100) : 0,
  }));
};

/** Største først, så navn. */
export const sortByRaised = <T extends { raisedOre: number; name: string }>(
  rows: T[] | null | undefined,
) =>
  [...(rows ?? [])].sort(
    (a, b) => b.raisedOre - a.raisedOre || a.name.localeCompare(b.name, "nb"),
  );

export const NO_CATEGORY = "Uten kategori";

/** Navnet som vises; null eller tom = «Uten kategori». */
export const categoryLabel = (category: string | null | undefined) =>
  category && category.trim() ? category.trim() : NO_CATEGORY;

/** Har listen minst én ekte kategori (ikke bare «Uten kategori»)? */
export const hasCategories = (list: CategorySummary[] | null | undefined) =>
  !!list?.some((c) => !!c && !!c.category && !!c.category.trim());

/** Vis kategoritabellen bare når det finnes kategoridata et sted. */
export const hasCategoryData = (summary: DonationSummary | null | undefined) =>
  !!summary &&
  (hasCategories(summary.byCategory) ||
    !!summary.byCampaign?.some((c) => hasCategories(c.byCategory)));

export interface CategoryRow extends CategorySummary {
  label: string;
  /** Andel av summen i listen, 0–100. */
  percent: number;
}

/**
 * Rader for kategoritabellen: største først, «Uten kategori» sist, med andel.
 * Manglende tall regnes som 0.
 */
export const categoryRows = (
  list: CategorySummary[] | null | undefined,
): CategoryRow[] => {
  const rows = (list ?? []).filter(Boolean).map((c) => ({
    category: c.category && c.category.trim() ? c.category.trim() : null,
    raisedOre: Number(c.raisedOre) || 0,
    chargeCount: Number(c.chargeCount) || 0,
    donorCount: Number(c.donorCount) || 0,
    activeMonthlyCount: Number(c.activeMonthlyCount) || 0,
  }));
  const total = rows.reduce((s, r) => s + r.raisedOre, 0);
  return rows
    .map((r) => ({
      ...r,
      label: categoryLabel(r.category),
      percent: total > 0 ? Math.round((r.raisedOre / total) * 100) : 0,
    }))
    .sort(
      (a, b) =>
        Number(a.category === null) - Number(b.category === null) ||
        b.raisedOre - a.raisedOre ||
        a.label.localeCompare(b.label, "nb"),
    );
};
